import React from 'react';
import { Box, Button, IconButton, Link, Typography } from '@mui/material';
import { IoChevronDown, IoChevronUp, IoClose } from 'react-icons/io5';
import { MdNotifications } from 'react-icons/md';
import GoBackButtonIcon from '@/assets/icons/GoBackButtonIcon';

export const PREVIEW_BLUE = '#195EFF';
export const PREVIEW_BLUE_SOFT = '#E8EFFF';
export const PREVIEW_AGENT_BUBBLE = '#EEF2FF';

export function PreviewScreenHeader({ onBack }: { onBack: () => void }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        pb: 1,
        borderBottom: '1px solid #E6EAF2',
        flexShrink: 0
      }}
    >
      <IconButton
        type='button'
        onClick={onBack}
        aria-label='Back to switch mode'
        sx={{
          p: 0,
          borderRadius: '50%',
          '&:hover': { bgcolor: 'transparent', opacity: 0.9 }
        }}
      >
        <GoBackButtonIcon arrowColor={PREVIEW_BLUE} borderColor={PREVIEW_BLUE_SOFT} />
      </IconButton>
      <Typography
        component='button'
        type='button'
        onClick={onBack}
        sx={{
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          p: 0,
          color: PREVIEW_BLUE,
          fontWeight: 500,
          fontSize: 15,
          fontFamily: 'inherit'
        }}
      >
        Back to Switch mode
      </Typography>
    </Box>
  );
}

export function PreviewKickFooter({
  organizationName,
  organizationLogoUrl
}: {
  organizationName?: string;
  organizationLogoUrl?: string | null;
}) {
  const poweredByLabel = organizationName ? `Powered by ${organizationName}` : 'Powered by Kickcall AI';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        pt: 2,
        mt: 'auto',
        borderTop: '1px solid #E6EAF2',
        flexShrink: 0,
        flexWrap: 'wrap'
      }}
    >
      {organizationLogoUrl ? (
        <Box
          component='img'
          src={organizationLogoUrl}
          alt={organizationName ? `${organizationName} logo` : 'Organization logo'}
          sx={{
            width: 40,
            height: 40,
            objectFit: 'contain',
            borderRadius: 1
          }}
        />
      ) : (
        <Box sx={{ fontWeight: 800, fontSize: 15, color: '#000' }}>
          <Box component='span' sx={{ display: 'block' }}>
            Kick
          </Box>
          <Box component='span' sx={{ display: 'block' }}>
            Call
          </Box>
        </Box>
      )}
      <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{poweredByLabel}</Typography>
    </Box>
  );
}

export function PreviewCard({ children, sx = {} }: { children: React.ReactNode; sx?: object }) {
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: '20px',
        boxShadow: '0px 20px 40px rgba(0, 0, 0, 0.12), 0px 4px 12px rgba(0, 0, 0, 0.08)',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: 400,
        mx: 'auto',
        minHeight: 0,
        ...sx
      }}
    >
      {children}
    </Box>
  );
}

export function PreviewFloatingClose({ onClick, label = 'Close' }: { onClick: () => void; label?: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
      <IconButton
        onClick={onClick}
        aria-label={label}
        sx={{
          width: 52,
          height: 52,
          bgcolor: PREVIEW_BLUE,
          color: '#fff',
          boxShadow: '0 8px 24px rgba(37, 99, 235, 0.35)',
          '&:hover': { bgcolor: '#1d4ed8' }
        }}
      >
        <IoClose size={28} />
      </IconButton>
    </Box>
  );
}

export function PreviewFloatingVoiceBar({ onMinimize, onEndCall }: { onMinimize: () => void; onEndCall: () => void }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
      <IconButton
        onClick={onMinimize}
        aria-label='Minimize'
        sx={{
          width: 52,
          height: 52,
          bgcolor: '#9CA3AF',
          color: '#fff',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          '&:hover': { bgcolor: '#6B7280' }
        }}
      >
        <IoChevronDown size={26} />
      </IconButton>
      <IconButton
        onClick={onEndCall}
        aria-label='End call'
        sx={{
          width: 52,
          height: 52,
          bgcolor: PREVIEW_BLUE,
          color: '#fff',
          boxShadow: '0 8px 24px rgba(37, 99, 235, 0.35)',
          '&:hover': { bgcolor: '#1d4ed8' }
        }}
      >
        <IoClose size={28} />
      </IconButton>
    </Box>
  );
}

export function PreviewSmsDisclaimer({
  embedded = false,
  organizationName
}: {
  embedded?: boolean;
  organizationName?: string;
}) {
  const displayOrganizationName = organizationName?.trim() || '';

  return (
    <Box
      sx={{
        mt: embedded ? 0 : 1.5,
        p: embedded ? 1.25 : 1.5,
        borderRadius: embedded ? '0 0 8px 8px' : 2,
        bgcolor: embedded ? '#F3F4F6' : '#F0F6FF',
        border: embedded ? 'none' : '1px solid #D0E3FF',
        borderTop: embedded ? '1px solid #E5E7EB' : undefined,
        display: 'flex',
        gap: embedded ? 1 : 1.25,
        alignItems: 'flex-start'
      }}
    >
      <MdNotifications size={embedded ? 16 : 22} color={PREVIEW_BLUE} style={{ flexShrink: 0, marginTop: 2 }} />
      <Typography sx={{ fontSize: 12, color: embedded ? '#6B7280' : '#4B5563', lineHeight: embedded ? 1.45 : 1.5 }}>
        By adding your phone number, you&apos;ll receive SMS updates from {displayOrganizationName} (AI agent notes
        &amp; appointment alerts). Message frequency may vary. Carrier charges may apply. By continuing, you agree to
        our{' '}
        <Link
          href='https://www.kickcall.ai/terms-conditions'
          underline='hover'
          sx={{ color: PREVIEW_BLUE, fontWeight: 600 }}
          target='_blank'
          rel='noopener noreferrer'
        >
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link
          href='https://www.kickcall.ai/privacy-policy'
          underline='hover'
          sx={{ color: PREVIEW_BLUE, fontWeight: 600 }}
          target='_blank'
          rel='noopener noreferrer'
        >
          Privacy Policy
        </Link>
        .
      </Typography>
    </Box>
  );
}

export function PreviewContinueButton({ disabled, onClick }: { disabled: boolean; onClick: () => void }) {
  return (
    <Button
      fullWidth
      variant='contained'
      disabled={disabled}
      onClick={onClick}
      sx={{
        mt: 2.5,
        mb: 2,
        py: 1.5,
        borderRadius: 999,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: 16,
        bgcolor: PREVIEW_BLUE,
        boxShadow: 'none',
        '&:hover': { bgcolor: '#1d4ed8', boxShadow: 'none' },
        '&.Mui-disabled': { bgcolor: '#93C5FD', color: '#fff' }
      }}
    >
      Continue
    </Button>
  );
}

export function PreviewAgentAvatarBubble({
  organizationLogoUrl,
  organizationName
}: {
  organizationLogoUrl?: string | null;
  organizationName?: string;
}) {
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 1,
        bgcolor: organizationLogoUrl ? 'transparent' : PREVIEW_BLUE,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
        py: 0.25
      }}
    >
      {organizationLogoUrl ? (
        <Box
          component='img'
          src={organizationLogoUrl}
          alt={organizationName ? `${organizationName} logo` : 'Organization logo'}
          sx={{
            width: 24,
            height: 24,
            objectFit: 'contain'
          }}
        />
      ) : (
        <>
          <Typography sx={{ fontSize: 8, fontWeight: 800, color: '#fff', letterSpacing: -0.3 }}>Kick</Typography>
          <Typography sx={{ fontSize: 8, fontWeight: 800, color: '#fff', letterSpacing: -0.3 }}>Call</Typography>
        </>
      )}
    </Box>
  );
}

export function PreviewMinimizedVoiceStrip({
  agentName,
  formattedTime,
  onExpand,
  onEndCall
}: {
  agentName: string;
  formattedTime: string;
  onExpand: () => void;
  onEndCall: () => void;
}) {
  return (
    <Box
      sx={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 16,
        maxWidth: 400,
        mx: 'auto',
        bgcolor: '#fff',
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.25,
        zIndex: 1100
      }}
    >
      <IconButton size='small' onClick={onExpand} aria-label='Expand' sx={{ color: PREVIEW_BLUE }}>
        <IoChevronUp size={22} />
      </IconButton>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontWeight={700} fontSize={14} noWrap>
          {agentName}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {formattedTime}
        </Typography>
      </Box>
      <IconButton
        size='small'
        onClick={onEndCall}
        aria-label='End call'
        sx={{ bgcolor: PREVIEW_BLUE, color: '#fff', '&:hover': { bgcolor: '#1d4ed8' } }}
      >
        <IoClose size={20} />
      </IconButton>
    </Box>
  );
}
